/**
 * 03/CRF/Create — `/countries/:cc/crfs/new` (Figma 12:4082, validation 12:10035).
 *
 * ===========================================================================
 * NOTHING IS SAVED UNTIL A BUTTON SAYS SO (OQ-W1)
 * ===========================================================================
 * There is no autosave. "Save Draft" is the only thing that writes, and the
 * footer's "✓ Draft saved {when}" appears ONLY after a save the server
 * acknowledged — never optimistically, never on a timer. The first save is
 * `createCrf`; every save after that is `updateCrf` against the same record,
 * and `crf-create.test.tsx` pins that (one create, then patches — never two
 * documents from one form).
 *
 * ===========================================================================
 * MONEY IS A STRING, END TO END
 * ===========================================================================
 * `amount` and `advance_amount` go to the API exactly as typed. The regex here
 * (`MONEY_PATTERN`, DECIMAL(14,2)'s shape) is a courtesy at the keystroke; the
 * server's `assertDecimalString` is the rule. Nothing in this file parses a
 * money value to a float. The one cross-field rule — an advance needs a quoted
 * total to be an advance against — is mirrored from `crfs.service.ts`
 * (`assertAdvanceCoherent`) so the operator hears it before the 422 would.
 *
 * ===========================================================================
 * CUSTOMIZATION DETAILS SURVIVE AN UNCHECK (deliberate, and tested)
 * ===========================================================================
 * Unchecking a value hides its details textarea but KEEPS the text in state,
 * keyed by platform id. Re-checking brings the words back; only the submit
 * payload filters to the checked set. Losing a paragraph to a stray click is the
 * failure mode; sending detail for an unchecked value would be the other one.
 *
 * ===========================================================================
 * THE FILES DROPZONE IS DISABLED-BEFORE-DRAFT, AND THAT IS NOT A D5 VIOLATION
 * ===========================================================================
 * D5 hides what a caller is not PERMITTED to do. Files-before-draft is a
 * STATE: the API has no CRF id to attach to until the first save, so the
 * dropzone renders disabled with the reason in a sentence. The D5 rule is
 * applied where it belongs — "Save & Submit to Development" needs `crf.edit`
 * and is ABSENT without it, and the whole page self-gates on `crf.create`.
 *
 * ===========================================================================
 * "REFERRED BY" IS PREFILLED FROM THE CUSTOMER, EDITABLE, AND NEVER REQUIRED
 * ===========================================================================
 * Owner ruling R4: *"CRF creation requiring advance/payment status/referral —
 * DROP. That blocks a coordinator at the start of a job over commercial facts
 * nobody knows yet. Capture them, never require them."* And the v11.0
 * amendment: referral *"belongs on the party, not only on the document."*
 *
 * So the durable fact — who sent us this customer — lives on `parties.referral`
 * and is typed once, on the New-customer page. THIS page reads it back the
 * moment a customer is picked (`PartyDto.referral` rides on every party read)
 * and PREFILLS its own "Referred by" field with it: the common case is zero
 * keystrokes, and the field says in words where the value came from. The field
 * stays EDITABLE because a job may come a different way from the customer
 * (`crfs.referral` is the per-document statement, and it can say `Direct`
 * against a customer whose referrer is `Shah & Co`). Picking a different
 * customer re-prefills; the field follows the customer, not the keystroke.
 *
 * ON THE WIRE, the field is OMITTED WHEN BLANK on the create — blank is no
 * statement, and the SERVER then inherits the party's (`CrfsService.create`),
 * so a client that never prefilled still records it. On the draft PATCH a blank
 * is sent as `null` (CLEARED): the draft already exists, and a coordinator who
 * emptied a prefilled field meant it. After every acknowledged save the field
 * re-syncs to the SERVER's value, because the server can legitimately hold
 * something other than the keystrokes — it INHERITS the customer's when the form
 * said nothing, and it trims — and the value on screen must be the value stored.
 * It does NOT re-case anything: the `direct` → `Direct` fold this paragraph used
 * to cite was struck by the owner on 2026-08-18 (`parties/referral.ts`).
 *
 * ⚠ IT IS NOT IN THE `errors` LIST, AND MUST NOT BE ADDED THERE. That `useMemo`
 * is the requiredness surface — the summary, the section badges, the footer and
 * the save/submit guard all read it — so a `referral` entry there would
 * re-impose the exact requirement the owner just dropped. `crf-create.test.tsx`
 * pins the absence in the same assertion that pins the presence of Customer and
 * Title, so the two cannot drift apart unnoticed.
 *
 * ===========================================================================
 * WHAT THE DESIGN SHOWS THAT THIS SCREEN DOES NOT BUILD
 * ===========================================================================
 * The design's Pricing section draws a line-item grid. The API stores
 * `amount` + `advance_amount` and nothing else, so this screen renders the two
 * fields and says "Line items arrive with a later phase." — an honest sentence
 * beats a grid wired to nothing.
 */

import { useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Upload } from 'lucide-react';

import { useCountryCode } from '../../auth/guards';
import { useSession } from '../../auth/session';
import { PageHeader } from '../../components/shell/AppShell';
import { Alert, Button, Checkbox, CountryBadge, TextField } from '../../components/ui';
import { SelectField } from '../../components/ui';
// WT5 — `inquiries/panel` died with the slide-over kill; TextAreaField moved
// to `components/ui` (import repoint only, byte-identical component).
import { TextAreaField } from '../../components/ui/TextAreaField';
import { useToast } from '../inquiries/toast';
import { PartyPicker } from '../quotes/PartyPicker';
import { fileKind, formatBytes } from '../work-pipeline/WorkFilesPanel';
import { fetchInquiries } from '../../lib/inquiries-api';
import { COUNTRIES, COUNTRY_ORDER, countryProfile } from '../../domain/countries';
import { relativeTime } from '../../domain/events';
import { cn } from '../../lib/cn';
import type { PartyDto } from '../inquiries/api';
import {
  CRF_FILE_ACCEPT,
  CRF_FILE_MAX_BYTES,
  confirmCrf,
  createCrf,
  crfKeys,
  fetchCrfFiles,
  fetchCrfPlatformOptions,
  updateCrf,
  uploadCrfFile,
} from './api';
import type { CreateCrfBody, CrfDto, CrfFileDto, CrfPlatformEntry, UpdateCrfBody } from './api';
import { crfErrorText } from './errors';

/* ─────────────────────────────────────────────────────────────── validation ── */

/**
 * DECIMAL(14,2)'s shape — twelve integer digits, at most two decimals. The same
 * courtesy-not-rule stance as the quote builder's `RATE_PATTERN`: the server's
 * `assertDecimalString` is the authority, this catches it at the keystroke.
 */
const MONEY_PATTERN = /^\d{1,12}(\.\d{1,2})?$/;

const MONEY_FORMAT_MESSAGE = 'Digits with at most two decimals, e.g. 18000 or 999.50';

/** Mirrored from the server's `assertAdvanceCoherent`, worded for the field. */
const ADVANCE_NEEDS_TOTAL = 'An advance needs a quoted total to be an advance against.';

function moneyFormatError(value: string): string | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  return MONEY_PATTERN.test(trimmed) ? null : MONEY_FORMAT_MESSAGE;
}

type SectionKey = 'customer' | 'customization' | 'pricing' | 'files';

interface ClientError {
  section: SectionKey;
  /** The label the summary sentence names — the field's own, verbatim. */
  label: string;
  message: string;
}

/* ──────────────────────────────────────────────────────────────── constants ── */

const SECTIONS: readonly { key: SectionKey; id: string; label: string }[] = [
  { key: 'customer', id: 'crf-section-customer', label: 'Customer & Inquiry' },
  { key: 'customization', id: 'crf-section-customization', label: 'Customization' },
  { key: 'pricing', id: 'crf-section-pricing', label: 'Pricing' },
  { key: 'files', id: 'crf-section-files', label: 'Files' },
];

/**
 * The currencies the group actually holds, from the §11 mirror — every
 * country's own plus CD's USD alternate, deduplicated in canonical order. The
 * select prefills with the CURRENT country's currency; the rest exist because
 * XM sells anywhere and CD legitimately quotes in USD.
 */
const CURRENCY_OPTIONS: readonly { value: string; label: string }[] = (() => {
  const seen = new Set<string>();
  const out: { value: string; label: string }[] = [];
  for (const code of COUNTRY_ORDER) {
    const profile = COUNTRIES[code];
    for (const currency of [profile.currency, profile.altCurrency]) {
      if (currency !== null && !seen.has(currency)) {
        seen.add(currency);
        out.push({ value: currency, label: currency });
      }
    }
  }
  return out;
})();

/* ─────────────────────────────────────────────────────────── small pieces ── */

/**
 * The per-section error count on a JumpNav item (A3). No precedent component
 * exists, so it is authored plainly from the danger token pair — the same pair
 * `TextField`'s error state already uses.
 */
function ErrorBadge({ count }: { count: number }): React.JSX.Element | null {
  if (count === 0) return null;
  return (
    <span
      aria-label={`${String(count)} ${count === 1 ? 'error' : 'errors'}`}
      className="tnum inline-flex min-w-[18px] items-center justify-center rounded-pill bg-danger-bg px-1.5 py-0.5 text-label font-medium text-danger-fg"
    >
      {count}
    </span>
  );
}

/** Dot + "Draft" — the header chip, up only once a draft exists server-side. */
function DraftChip(): React.JSX.Element {
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-pill bg-subtle px-2.5 py-1 text-label font-medium text-fg-secondary ring-1 ring-line">
      <span aria-hidden="true" className="size-1.5 shrink-0 rounded-full bg-current" />
      Draft
    </span>
  );
}

function SectionCard({
  id,
  label,
  children,
}: {
  id: string;
  label: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <section
      id={id}
      aria-label={label}
      // scroll-mt so a JumpNav landing is not hidden under anything sticky.
      className="flex scroll-mt-6 flex-col gap-3 rounded-md border border-line bg-surface p-5"
    >
      <h2 className="text-th uppercase text-fg-secondary">{label}</h2>
      {children}
    </section>
  );
}

/* ───────────────────────────────────────────────────────────────── screen ── */

export function CrfCreatePage(): React.JSX.Element {
  const countryCode = useCountryCode();
  const { can } = useSession();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  const canCreate = can(countryCode, 'crf.create');
  const canSubmit = can(countryCode, 'crf.edit');

  const [party, setParty] = useState<PartyDto | null>(null);
  const [title, setTitle] = useState('');
  const [inquiryId, setInquiryId] = useState('');
  /**
   * "Referred by" — HOW THIS JOB CAME IN (`crfs.referral`, migration 49). PREFILLED from
   * the chosen customer's own `referral` the moment a customer is picked (see the file
   * header, "REFERRED BY IS PREFILLED…"), editable, and NEVER REQUIRED: it is deliberately
   * absent from the `errors` list below, which is the requiredness surface.
   */
  const [referral, setReferral] = useState('');
  /** Which platform ids are checked — the payload filters on THIS. */
  const [platformOn, setPlatformOn] = useState<Readonly<Record<string, boolean>>>({});
  /** Detail text per platform id, kept across uncheck/recheck. See the file header. */
  const [platformDetail, setPlatformDetail] = useState<Readonly<Record<string, string>>>({});
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState(() => countryProfile(countryCode).currency);
  const [advance, setAdvance] = useState('');

  /** The server-side draft, once one exists. Its id is what updates and files ride. */
  const [draft, setDraft] = useState<CrfDto | null>(null);
  /** ISO instant of the last save the SERVER acknowledged. Never set client-side-only. */
  const [savedAt, setSavedAt] = useState<string | null>(null);
  /** A3: the summary/badges appear only after a save or submit was attempted. */
  const [attempted, setAttempted] = useState(false);
  const [busy, setBusy] = useState<'save' | 'submit' | null>(null);
  /** One page-level Alert carries the server's own sentence (400/409/422 pass through). */
  const [serverError, setServerError] = useState<string | null>(null);

  /**
   * The Linked-inquiry options — the country's inquiry list, first page. A raw
   * id input is not acceptable UX and no inquiry picker precedent exists, so
   * this is a plain select over the list route the inquiries screens already
   * use. 50 rows is a pragmatic first page, and the hint says the link is
   * optional either way.
   */
  const inquiries = useQuery({
    queryKey: ['crfs', countryCode, 'inquiry-options'],
    queryFn: ({ signal }) => fetchInquiries(countryCode, { page: 1, perPage: 50 }, signal),
    enabled: canCreate,
    staleTime: 60_000,
  });

  /*
   * THE CUSTOMIZATION VOCABULARY IS FETCHED, NOT LISTED (migration 38, owner Q8).
   *
   * What stood here was `CRF_MODULES.map(...)` over four checkboxes hardcoded in
   * `domain.ts` — a hand copy of the server's list, which is the mirror the owner
   * ruled out: two vocabularies for one idea is the defect he named. The options
   * now come from the server ALREADY FILTERED to what this country may newly
   * attach to a CRF, so retired values and values that do not apply to CRF work
   * are simply not offered.
   *
   * ⚠ AN EMPTY OR FAILED FETCH IS SAID IN WORDS, NEVER RENDERED AS "no options".
   * A picker that silently shows nothing is indistinguishable from a vocabulary
   * with nothing in it, and the operator would file the CRF without its scope.
   */
  const platformOptions = useQuery({
    // ⚠ `'crf'` NAMED EXPLICITLY, not left to the server's default. This screen creates
    // CRFs; asking for the CRF applicability says so, and the day a project create screen
    // exists it asks for its own rather than inheriting a default that happens to fit.
    queryKey: crfKeys.platformOptions(countryCode, 'crf'),
    queryFn: ({ signal }) => fetchCrfPlatformOptions(countryCode, 'crf', signal),
    enabled: canCreate,
    staleTime: 60_000,
  });

  /**
   * Every client rule, computed fresh each render — the one list all A3 surfaces read.
   *
   * ⚠ THIS IS THE REQUIREDNESS SURFACE. Customer and Title are the ONLY required facts on
   * a CRF (owner ruling R4; Train B pinned the 201 on `{party_id, title}` alone). The money
   * entries below are FORMAT/coherence rules on values the operator chose to type, not
   * requirements. `referral` (and `advance`, and payment status) MUST NOT be pushed here —
   * an entry in this list is a refusal to save, which is precisely what the owner struck.
   */
  const errors = useMemo<readonly ClientError[]>(() => {
    const list: ClientError[] = [];
    if (party === null) {
      list.push({
        section: 'customer',
        label: 'Customer',
        message: 'Pick the customer this CRF is for.',
      });
    }
    if (title.trim() === '') {
      list.push({ section: 'customer', label: 'Title', message: 'A title is required.' });
    }
    const amountFormat = moneyFormatError(amount);
    if (amountFormat !== null) {
      list.push({ section: 'pricing', label: 'Quoted total', message: amountFormat });
    }
    const advanceFormat = moneyFormatError(advance);
    if (advanceFormat !== null) {
      list.push({ section: 'pricing', label: 'Advance amount', message: advanceFormat });
    } else if (advance.trim() !== '' && amount.trim() === '') {
      list.push({ section: 'pricing', label: 'Advance amount', message: ADVANCE_NEEDS_TOTAL });
    }
    return list;
  }, [party, title, amount, advance]);

  const errorCount = errors.length;
  const sectionErrorCount = (key: SectionKey): number =>
    errors.filter((error) => error.section === key).length;

  /* ── the designed refusal — the page self-gates on `crf.create` ─────────── */
  if (!canCreate) {
    return (
      <div className="flex flex-col gap-4 p-6">
        <Alert tone="danger" title="CRFs cannot be raised from this account">
          Creating a CRF in {countryCode} needs the CRF Create permission. Ask an
          administrator to widen your access.
        </Alert>
        <Link
          to={`/countries/${countryCode}/crfs`}
          className="w-fit text-body13 text-fg underline underline-offset-2 hover:decoration-2"
        >
          Back to the CRF register
        </Link>
      </div>
    );
  }

  /* ── payload + persistence ──────────────────────────────────────────────── */

  const chosenPlatforms = (): CrfPlatformEntry[] =>
    (platformOptions.data ?? [])
      .filter((option) => platformOn[option.id] === true)
      .map((option) => {
        const detail = (platformDetail[option.id] ?? '').trim();
        return { platform_id: option.id, detail: detail === '' ? null : detail };
      });

  /**
   * Create on the first save, patch after. Returns the server's record; the
   * caller decides what acknowledging it means (stay, or go on to confirm).
   * `platforms` is ALWAYS sent — the checked set is a whole statement, and `[]`
   * deliberately means "cleared" on the PATCH (the api.ts contract).
   */
  const persistDraft = async (chosenParty: PartyDto): Promise<CrfDto> => {
    const trimmedAmount = amount.trim();
    const trimmedReferral = referral.trim();
    const shared = {
      title: title.trim(),
      inquiry_id: inquiryId === '' ? null : inquiryId,
      amount: trimmedAmount === '' ? null : trimmedAmount,
      // Currency only means something beside a figure; alone it would be a claim.
      currency: trimmedAmount === '' ? null : currency,
      advance_amount: advance.trim() === '' ? null : advance.trim(),
      platforms: chosenPlatforms(),
    };
    if (draft === null) {
      /*
       * `referral` is OMITTED WHEN BLANK on the create, deliberately and unlike the money
       * fields beside it: absence tells the server "no statement", and the server then
       * inherits the customer's own referral (`CrfsService.create`). Sending `null` would
       * mean the same thing on this route, but omission is the honest spelling and it is
       * what the api.ts contract documents. A typed value is the document's own statement.
       */
      const body: CreateCrfBody = {
        party_id: chosenParty.id,
        ...shared,
        ...(trimmedReferral === '' ? {} : { referral: trimmedReferral }),
      };
      return createCrf(countryCode, body);
    }
    /*
     * On the PATCH a blank is CLEARED (`null`), not omitted: the draft already carries a
     * value (typed or inherited), the field was prefilled from it after the save, and a
     * coordinator who emptied it meant "none". `undefined` would leave the row untouched
     * and the screen would show a blank the record does not have.
     */
    const body: UpdateCrfBody = {
      ...shared,
      referral: trimmedReferral === '' ? null : trimmedReferral,
    };
    return updateCrf(draft.id, body);
  };

  const acknowledgeSave = (record: CrfDto): void => {
    setDraft(record);
    setSavedAt(new Date().toISOString());
    /*
     * The one field that re-syncs from the server's echo. The server NORMALISES `referral`
     * (trim, blank → NULL — and NOTHING else: the `direct` → `Direct` fold this line used
     * to name was struck by the owner on 2026-08-18, `parties/referral.ts`) and INHERITS the
     * customer's when the form said nothing, so after a save the row can legitimately hold
     * something other than what was typed. The value on screen must be the value stored;
     * a form showing "" over a row that says "Shah & Co" would be the quietest defect here.
     * `?? ''` covers a response without the key (the type is optional for fixture reasons).
     */
    setReferral(record.referral ?? '');
    void queryClient.invalidateQueries({ queryKey: ['crfs', countryCode] });
  };

  const saveDraft = async (): Promise<void> => {
    if (errorCount > 0 || party === null) {
      setAttempted(true);
      return;
    }
    setBusy('save');
    setServerError(null);
    try {
      const record = await persistDraft(party);
      acknowledgeSave(record);
      toast.push({ text: `Draft ${record.doc_no} saved.` });
    } catch (error) {
      setServerError(crfErrorText(error));
    } finally {
      setBusy(null);
    }
  };

  const saveAndSubmit = async (): Promise<void> => {
    if (errorCount > 0 || party === null) {
      setAttempted(true);
      return;
    }
    setBusy('submit');
    setServerError(null);
    try {
      const record = await persistDraft(party);
      // The save succeeded whatever the confirm does next — say so truthfully.
      acknowledgeSave(record);
      try {
        const confirmed = await confirmCrf(record.id);
        toast.push({ text: `${confirmed.doc_no} submitted to development.` });
        navigate(`/countries/${countryCode}/crfs/${confirmed.id}`);
      } catch (error) {
        setServerError(crfErrorText(error));
      }
    } catch (error) {
      setServerError(crfErrorText(error));
    } finally {
      setBusy(null);
    }
  };

  /* ── field-level errors ─────────────────────────────────────────────────── */

  // Format errors are live (the quote-builder keystroke courtesy); the advance
  // coherence rule is live too because both of its inputs are on screen.
  // Requiredness appears after an attempt — an empty form is not yet wrong.
  const titleError = attempted && title.trim() === '' ? 'A title is required.' : null;
  const customerMissing = attempted && party === null;
  const amountError = moneyFormatError(amount);
  const advanceError =
    moneyFormatError(advance) ??
    (advance.trim() !== '' && amount.trim() === '' ? ADVANCE_NEEDS_TOTAL : null);

  const showProblems = attempted && errorCount > 0;
  const summaryLabels = errors.map((error) => error.label).join(', ');

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={party === null ? 'New CRF' : `New CRF — ${party.name}`}
        badge={
          <>
            <CountryBadge code={countryCode} />
            {draft === null ? null : <DraftChip />}
          </>
        }
        subtitle={`A customisation request for ${countryCode}. Nothing is saved until you press Save Draft.`}
      />

      <div className="flex flex-col gap-3 px-6">
        {showProblems ? (
          <Alert
            tone="danger"
            title={`Can't submit yet — ${String(errorCount)} ${
              errorCount === 1 ? 'field needs' : 'fields need'
            } attention: ${summaryLabels}.`}
          />
        ) : null}

        {serverError === null ? null : (
          <Alert tone="danger" title="The server refused that">
            {serverError}
          </Alert>
        )}
      </div>

      <div className="flex items-start gap-6 px-6 pb-6">
        {/* ── JumpNav — desktop only, 220px, plain tokens (no precedent) ────── */}
        <nav
          aria-label="On this page"
          className="sticky top-6 hidden w-[220px] shrink-0 flex-col gap-0.5 lg:flex"
        >
          {SECTIONS.map((section) => {
            const count = attempted ? sectionErrorCount(section.key) : 0;
            return (
              <a
                key={section.key}
                href={`#${section.id}`}
                onClick={(event) => {
                  event.preventDefault();
                  document
                    .getElementById(section.id)
                    ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                className="flex items-center justify-between gap-2 rounded-sm px-2.5 py-1.5 text-body13 text-fg-secondary hover:bg-subtle hover:text-fg"
              >
                {section.label}
                <ErrorBadge count={count} />
              </a>
            );
          })}
        </nav>

        {/* ── the four sections ─────────────────────────────────────────────── */}
        <div className="flex min-w-0 flex-1 flex-col gap-5">
          <SectionCard id="crf-section-customer" label="Customer & Inquiry">
            {draft === null ? (
              <PartyPicker
                countryCode={countryCode}
                party={party}
                onSelect={(chosen) => {
                  setParty(chosen);
                  /*
                   * THE PREFILL — the customer record's own answer to "who sent us this
                   * customer" becomes this CRF's starting answer, in the field, editable.
                   * It follows the CUSTOMER, not the keystroke: choosing a customer with
                   * no recorded referrer blanks the field rather than carrying the last
                   * customer's referrer across, which would be the quietest wrong answer
                   * on the form. `?? ''` covers a party payload without the key.
                   */
                  setReferral(chosen.referral ?? '');
                }}
                onClear={() => setParty(null)}
              />
            ) : (
              // The API has no route that moves a CRF to another party, so once
              // a draft exists the choice is stated rather than re-offered.
              <div className="flex items-center justify-between gap-3 rounded-sm border border-line bg-subtle px-3 py-2">
                <div className="flex min-w-0 flex-col gap-0.5">
                  <span className="truncate text-body font-medium text-fg">
                    {party?.name ?? draft.party_id}
                  </span>
                  <span className="text-label text-fg-secondary">
                    The customer is fixed once a draft exists.
                  </span>
                </div>
                <CountryBadge code={countryCode} />
              </div>
            )}
            {customerMissing ? (
              <p className="text-label text-danger-fg">Pick the customer this CRF is for.</p>
            ) : null}

            {/*
              * "REFERRED BY" — how THIS job came in (`crfs.referral`), prefilled from the
              * customer's durable answer (`parties.referral`) and editable. In the CUSTOMER
              * group, not Pricing: it is a fact about who brought the work, not a figure.
              * NEVER REQUIRED — it is not in `errors`, has no error prop, and blank saves
              * (the server inherits the customer's; see the file header). The line beneath
              * the field is the READ-ONLY display of the customer record's value, so the
              * operator can see what was prefilled even after editing it.
              */}
            <div className="flex flex-col gap-1.5">
              <TextField
                label="Referred by"
                value={referral}
                maxLength={200}
                placeholder="A person, a firm, a partner — or Direct"
                onChange={(event) => setReferral(event.target.value)}
              />
              <p className="text-label text-fg-secondary" data-testid="crf-referral-source">
                {party === null ? (
                  'Optional. Pick the customer first — their record’s answer prefills here.'
                ) : party.referral === null || party.referral === undefined ? (
                  <>
                    Not recorded on {party.name}&rsquo;s customer record. Optional — say who
                    sent this job if you know, or leave it blank.
                  </>
                ) : (
                  <>
                    Prefilled from the customer record:{' '}
                    <span className="font-medium text-fg" data-testid="crf-referral-party-value">
                      {party.referral}
                    </span>
                    . Change it only if this job came a different way.
                  </>
                )}
              </p>
            </div>

            <TextField
              label="Title"
              value={title}
              error={titleError}
              placeholder="What this customisation is, in one line"
              maxLength={300}
              onChange={(event) => setTitle(event.target.value)}
            />

            {inquiries.isPending ? (
              <p className="text-label text-fg-secondary">Loading the inquiry list…</p>
            ) : inquiries.isError ? (
              <p className="text-label text-fg-secondary">
                The inquiry list could not be read, so linking an inquiry is not offered
                right now. The link can be added later from the record.
              </p>
            ) : (inquiries.data?.data ?? []).length === 0 ? (
              <p className="text-label text-fg-secondary">
                There are no inquiries in {countryCode} to link. That is fine — the link is
                optional.
              </p>
            ) : (
              <SelectField
                label="Linked inquiry"
                hint="Optional — the inquiry this CRF grew out of."
                value={inquiryId}
                onChange={setInquiryId}
                options={[
                  { value: '', label: 'None' },
                  ...(inquiries.data?.data ?? []).map((inquiry) => ({
                    value: inquiry.id,
                    label: `${inquiry.doc_no} — ${
                      inquiry.subject === null || inquiry.subject === ''
                        ? (inquiry.party_name ?? 'No subject')
                        : inquiry.subject
                    }`,
                  })),
                ]}
              />
            )}
          </SectionCard>

          <SectionCard id="crf-section-customization" label="Customization">
            <div className="flex flex-col gap-3">
              {platformOptions.isPending ? (
                <p className="text-label text-fg-secondary">Loading the list…</p>
              ) : platformOptions.isError ? (
                <p className="text-label text-fg-secondary">
                  The customization list could not be read, so nothing can be ticked right
                  now. It can be recorded later from the record.
                </p>
              ) : (platformOptions.data ?? []).length === 0 ? (
                <p className="text-label text-fg-secondary">
                  Nothing is offered in {countryCode} yet. The list is maintained by the
                  group and switched on per country.
                </p>
              ) : (
                (platformOptions.data ?? []).map((option) => {
                  const checked = platformOn[option.id] === true;
                  return (
                    <div key={option.id} className="flex flex-col gap-2">
                      <Checkbox
                        label={option.name}
                        checked={checked}
                        onChange={(event) =>
                          setPlatformOn((current) => ({
                            ...current,
                            [option.id]: event.target.checked,
                          }))
                        }
                      />
                      {checked ? (
                        <TextAreaField
                          label={`${option.name} — details`}
                          rows={3}
                          hint="What exactly is wanted. Kept if you untick and re-tick."
                          value={platformDetail[option.id] ?? ''}
                          onChange={(event) =>
                            setPlatformDetail((current) => ({
                              ...current,
                              [option.id]: event.target.value,
                            }))
                          }
                        />
                      ) : null}
                    </div>
                  );
                })
              )}
            </div>
          </SectionCard>

          <SectionCard id="crf-section-pricing" label="Pricing">
            <div className="flex flex-wrap gap-3">
              <div className="w-44">
                <TextField
                  label="Quoted total"
                  inputMode="decimal"
                  value={amount}
                  error={amountError}
                  onChange={(event) => setAmount(event.target.value)}
                />
              </div>
              <div className="w-36">
                <SelectField
                  label="Currency"
                  options={[...CURRENCY_OPTIONS]}
                  value={currency}
                  onChange={setCurrency}
                />
              </div>
              <div className="w-44">
                <TextField
                  label="Advance amount"
                  inputMode="decimal"
                  value={advance}
                  error={advanceError}
                  onChange={(event) => setAdvance(event.target.value)}
                />
              </div>
            </div>
            <p className="text-label text-fg-secondary">
              Line items arrive with a later phase.
            </p>
          </SectionCard>

          <SectionCard id="crf-section-files" label="Files">
            <CrfFilesSection draft={draft} />
          </SectionCard>
        </div>
      </div>

      {/* ── sticky footer — BulkBar's z discipline (under the z-50 toasts) ──── */}
      <footer className="sticky bottom-0 z-40 mt-auto flex flex-wrap items-center justify-between gap-3 border-t border-line bg-surface px-6 py-3">
        <Link
          to={`/countries/${countryCode}/crfs`}
          className="inline-flex h-9 items-center rounded-sm border border-line px-3 text-body text-fg-secondary hover:bg-subtle hover:text-fg"
        >
          Cancel
        </Link>

        <p aria-live="polite" className="min-w-0 text-label">
          {showProblems ? (
            <span className="font-medium text-danger-fg">
              {`${String(errorCount)} ${errorCount === 1 ? 'error' : 'errors'} — fix before submitting`}
            </span>
          ) : savedAt !== null ? (
            <span className="text-fg-secondary">✓ Draft saved {relativeTime(savedAt)}</span>
          ) : null}
        </p>

        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            disabled={busy !== null}
            onClick={() => {
              void saveDraft();
            }}
          >
            {busy === 'save' ? 'Saving…' : 'Save Draft'}
          </Button>
          {canSubmit ? (
            <Button
              disabled={busy !== null}
              onClick={() => {
                void saveAndSubmit();
              }}
            >
              {busy === 'submit' ? 'Submitting…' : 'Save & Submit to Development'}
            </Button>
          ) : (
            // D5: the gated action is absent, and the absence is explained in
            // words rather than left as a mystery.
            <p className="text-label text-fg-secondary">
              Submitting to development needs the CRF Edit permission.
            </p>
          )}
        </div>
      </footer>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────── files ── */

/**
 * The dropzone + rows, visuals per `WorkFilesPanel` (Figma 12:5281 / 12:5586).
 *
 * Before a draft exists this is a disabled STATE with the reason in a sentence
 * — files attach to a CRF id, and there is none yet. That is not a permission
 * gate, so D5's absent-not-disabled rule does not apply here (see file header).
 */
function CrfFilesSection({ draft }: { draft: CrfDto | null }): React.JSX.Element {
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  /** A refusal made HERE, before any request — currently only the size cap. */
  const [localRefusal, setLocalRefusal] = useState<string | null>(null);

  const draftId = draft?.id ?? null;

  const files = useQuery({
    queryKey: crfKeys.files(draftId ?? 'none'),
    queryFn: ({ signal }): Promise<CrfFileDto[]> =>
      draftId === null ? Promise.reject(new Error('no draft')) : fetchCrfFiles(draftId, signal),
    enabled: draftId !== null,
  });

  const upload = useMutation({
    mutationFn: (file: File): Promise<CrfFileDto> =>
      draftId === null ? Promise.reject(new Error('no draft')) : uploadCrfFile(draftId, file),
    onSuccess: async () => {
      if (draftId !== null) {
        await queryClient.invalidateQueries({ queryKey: crfKeys.files(draftId) });
      }
    },
  });

  const send = (file: File | undefined): void => {
    if (file === undefined || draftId === null) return;
    setLocalRefusal(null);
    if (file.size > CRF_FILE_MAX_BYTES) {
      // Refused here rather than sent-and-refused — the server still enforces
      // the cap; this spares the operator an upload that was never going to land.
      setLocalRefusal(
        `${file.name} is ${formatBytes(file.size)}. The limit is ${formatBytes(CRF_FILE_MAX_BYTES)}, so it was not sent.`,
      );
      return;
    }
    upload.mutate(file);
  };

  if (draftId === null) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-md border border-dashed border-line-strong bg-subtle/40 px-4 py-6 text-center">
        <Upload aria-hidden="true" className="size-5 text-fg-muted" />
        <p className="text-body13 text-fg-secondary">
          Save the draft first — files attach to a saved CRF.
        </p>
        <button
          type="button"
          disabled
          className="rounded-sm border border-line bg-surface px-2.5 py-1 text-body13 font-medium text-fg disabled:opacity-60"
        >
          Choose a file
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div
        // A drop target, not a control — the BUTTON inside is what keyboard and
        // screen-reader users operate (the WorkFilesPanel ruling).
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          send(event.dataTransfer.files[0]);
        }}
        data-testid="crf-files-dropzone"
        className={cn(
          'flex flex-col items-center gap-2 rounded-md border border-dashed px-4 py-6 text-center',
          dragging ? 'border-indigo bg-subtle' : 'border-line-strong bg-subtle/40',
        )}
      >
        <Upload aria-hidden="true" className="size-5 text-fg-muted" />
        <p className="text-body13 text-fg">
          Drop a PDF, ZIP, image or TDL here — up to {formatBytes(CRF_FILE_MAX_BYTES)}.
        </p>
        <button
          type="button"
          disabled={upload.isPending}
          onClick={() => inputRef.current?.click()}
          className="rounded-sm border border-line bg-surface px-2.5 py-1 text-body13 font-medium text-fg hover:border-line-strong disabled:opacity-60"
        >
          {upload.isPending ? 'Uploading…' : 'Choose a file'}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept={CRF_FILE_ACCEPT}
          className="hidden"
          onChange={(event) => {
            send(event.target.files?.[0]);
            // Cleared so the SAME file can be chosen again after a failure.
            event.target.value = '';
          }}
        />
      </div>

      {localRefusal === null ? null : (
        <Alert tone="warning" title="That file was not sent">
          {localRefusal}
        </Alert>
      )}
      {upload.isError ? (
        <Alert tone="danger" title="The upload was refused">
          {crfErrorText(upload.error)}
        </Alert>
      ) : null}

      {files.isPending ? (
        <p className="text-body13 text-fg-muted">Loading the attached files…</p>
      ) : files.isError ? (
        <Alert tone="danger" title="The file list could not be read">
          {crfErrorText(files.error)} Nothing below is a count of what is attached.
        </Alert>
      ) : (files.data ?? []).length === 0 ? (
        <p className="text-body13 text-fg-muted">Nothing attached yet.</p>
      ) : (
        <table className="w-full text-left text-body13">
          <thead>
            <tr className="border-b border-line text-label text-fg-muted">
              <th scope="col" className="py-1.5 font-medium">
                File
              </th>
              <th scope="col" className="py-1.5 font-medium">
                Type
              </th>
              <th scope="col" className="py-1.5 text-right font-medium">
                Size
              </th>
              <th scope="col" className="py-1.5 text-right font-medium">
                Attached
              </th>
            </tr>
          </thead>
          <tbody>
            {(files.data ?? []).map((file) => (
              <tr key={file.id} className="border-b border-line last:border-0">
                <td className="py-2 text-fg">
                  <span className="truncate">{file.file_name}</span>
                </td>
                {/* The extension is a fact; a MIME type for a TDL is not (ESC-14). */}
                <td className="py-2 text-fg-muted">{fileKind(file.file_name)}</td>
                <td className="tnum py-2 text-right text-fg-muted">
                  {formatBytes(file.size_bytes)}
                </td>
                <td className="tnum py-2 text-right text-fg-muted">
                  {new Date(file.created_at).toLocaleDateString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
